import { useCallback, useEffect } from "react";
import { cloneDeep } from "lodash";

import type { ProfileClinical } from "types/profile";

import useAppContext from "../../AppContext";
import ContentGutter from "../../ContentGutter";
import SiteWrapper from "../../SiteWrapper";
import AccountCreateModal from "../AccountCreateModal";
import ClinicalEligible from "./ClinicalEligible";
import ClinicalIneligible from "./ClinicalIneligible";
import ClinicalIneligibleSuicide from "./ClinicalIneligibleSuicide";
import Error from "../Error";
import useUrlSlugContext from "components/UrlSlugContext";
import RedirectFirstQuestion from "components/RedirectFirstQuestion";

import { saveResultsCookie } from "../storeResultsData";
import useCreateClinicSub from "../../../utils/useCreateClinicSub";
import { useSubmitAssessment } from "../../../utils/assessment";
import { hasAnsweredAllQuestions } from "../../../utils/branching";
import { setPageData, dispatchEvent, pageView } from "../../../utils/analytics";
import getSessionData from "../../../utils/getSessionData";
import {
  CLINICAL_ELIGIBLE,
  CLINICAL_INELIGIBLE,
  resultToEventMap
} from "../../../utils/resultFunctions/constants";
import LoadingInterstitial from "../LoadingInterstitial";
import { WL_REASON_ELI_SUICIDE } from "utils/eligibility";

const ResultsClinical = (): JSX.Element => {
  const {
    profile,
    setProfile,
    newQuiz,
    answers,
    contact,
    assessment,
    authData,
    nextStack
  } = useAppContext();
  const { baseUrl, urlSlug } = useUrlSlugContext();
  const { loggedInAs } = authData || {};
  const { market } = contact || {};
  const {
    assessmentId,
    isSuccess,
    isFailure,
    submit,
    isSubmitting,
    eligibleSequence,
    resultsScreen,
  } = useSubmitAssessment();
  const { createIneligibleUser } = useCreateClinicSub();
  const isAssessmentComplete = hasAnsweredAllQuestions(answers, nextStack);
  const isFinished = !!loggedInAs && !isSubmitting && isSuccess;

  const handleSubmit = useCallback(() => {
    if (!isAssessmentComplete || !loggedInAs) {
      return;
    }

    submit({
      answers,
      newQuiz,
      questions: assessment?.questions,
      createIneligibleUser,
      profile,
      market
    });
  }, [isAssessmentComplete, loggedInAs]);

  useEffect(() => {
    pageView("result");
  }, []);

  useEffect(() => {
    handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    if (
      !isAssessmentComplete ||
      !loggedInAs ||
      !isSuccess ||
      !assessmentId ||
      !setProfile
    ) {
      return;
    }

    const clonedProfile: Partial<ProfileClinical> = cloneDeep(profile || {});

    // Save results cookie for results restored page.
    saveResultsCookie({
      data: {
        aid: assessmentId,
        profile: clonedProfile
      },
      baseUrl
    });

    // Log event for clinical eligible/ineligible.
    const { uuid } = getSessionData();
    const result = eligibleSequence ? CLINICAL_ELIGIBLE : CLINICAL_INELIGIBLE;

    dispatchEvent({
      event: "non_click_event",
      event_key: "assessment_end",
      event_category: "assessment",
      event_action: "assesment_end",
      user_id: uuid,
      assessment_type: urlSlug,
      assessment_result: resultToEventMap[result]
    });

    setPageData({
      assessment_result: resultToEventMap[result]
    });
  }, [assessmentId, loggedInAs, isAssessmentComplete, isSuccess, setProfile]);

  if (!isAssessmentComplete) {
    return <RedirectFirstQuestion />;
  }

  if (isFailure) {
    return <Error retry={handleSubmit} />;
  }

  return (
    <>
      <LoadingInterstitial />
      <ContentGutter>
        <SiteWrapper size="small">
          <AccountCreateModal />
          {isFinished && eligibleSequence && <ClinicalEligible />}
          {isFinished && !eligibleSequence && resultsScreen === WL_REASON_ELI_SUICIDE && <ClinicalIneligibleSuicide/>}
          {isFinished && !eligibleSequence && resultsScreen !== WL_REASON_ELI_SUICIDE && <ClinicalIneligible />}
        </SiteWrapper>
      </ContentGutter>
    </>
  );
};

export default ResultsClinical;
